import { advanceOrderedProgress } from './ordered-progress'
import type { RacerProgress, RaceState } from './types'

const DEFAULT_CHECKPOINT_COUNT = 12
const COUNTDOWN_SECONDS = 3
const FINAL_CALL_SECONDS = 10

function createProgress(vehicleId: string): RacerProgress {
  return {
    vehicleId,
    lap: 0,
    lastCheckpoint: 0,
    nextCheckpoint: 1,
    lapStartedAt: 0,
    bestLap: null,
    finishedAt: null,
    recordStatus: 'racing',
  }
}

export function createRaceState(
  vehicleIds: string[],
  totalLaps = 3,
  playerId = 'player',
  checkpointCount = DEFAULT_CHECKPOINT_COUNT,
): RaceState {
  return {
    phase: 'countdown',
    countdown: COUNTDOWN_SECONDS,
    elapsed: 0,
    totalLaps,
    checkpointCount,
    playerId,
    vehicleIds: [...vehicleIds],
    progress: Object.fromEntries(vehicleIds.map((vehicleId) => [vehicleId, createProgress(vehicleId)])),
    finishOrder: [],
    finishCountdown: null,
  }
}

export function synchronizeRaceStart(race: RaceState, startsAt: number, now: number): RaceState {
  if (!Number.isFinite(startsAt) || !Number.isFinite(now)) {
    throw new RangeError('Race start epochs must be finite numbers')
  }
  const countdown = Math.max(0, (startsAt - now) / 1_000)
  if (countdown > 0) {
    return { ...race, phase: 'countdown', countdown, elapsed: 0 }
  }
  return {
    ...race,
    phase: 'racing',
    countdown: 0,
    elapsed: Math.max(0, (now - startsAt) / 1_000),
  }
}

export function stepRaceClock(race: RaceState, dt: number): RaceState {
  if (race.phase === 'finished') return race
  const safeDt = Math.max(0, dt)
  if (race.phase === 'countdown') {
    const countdown = Math.max(0, race.countdown - safeDt)
    if (countdown > 0) return { ...race, countdown }
    return {
      ...race,
      phase: 'racing',
      countdown: 0,
      elapsed: race.elapsed + Math.max(0, safeDt - race.countdown),
    }
  }
  if (race.phase === 'finish-countdown') {
    const finishCountdown = Math.max(0, (race.finishCountdown ?? 0) - safeDt)
    const elapsed = race.elapsed + safeDt
    if (finishCountdown > 0) return { ...race, finishCountdown, elapsed }
    return {
      ...race,
      phase: 'finished',
      finishCountdown: 0,
      elapsed,
      progress: Object.fromEntries(
        Object.entries(race.progress).map(([vehicleId, progress]) => [
          vehicleId,
          progress.recordStatus === 'racing' ? { ...progress, recordStatus: 'unrecorded' as const } : progress,
        ]),
      ),
    }
  }
  return { ...race, elapsed: race.elapsed + safeDt }
}

export function advanceCheckpoint(race: RaceState, vehicleId: string, checkpoint: number, now: number): RaceState {
  if (race.phase === 'countdown' || race.phase === 'finished') return race
  const current = race.progress[vehicleId]
  if (!current) return race
  const next = advanceOrderedProgress(current, checkpoint, now, race.totalLaps, race.checkpointCount)
  if (next === current) return race

  let finishOrder = race.finishOrder
  let phase: RaceState['phase'] = race.phase
  let finishCountdown = race.finishCountdown
  if (next.recordStatus === 'finished') {
    finishOrder = [...finishOrder, vehicleId]
    if (finishOrder.length === race.vehicleIds.length) {
      phase = 'finished'
      finishCountdown = 0
    } else if (race.finishOrder.length === 0) {
      phase = 'finish-countdown'
      finishCountdown = FINAL_CALL_SECONDS
    }
  }

  return {
    ...race,
    phase,
    finishOrder,
    finishCountdown,
    progress: {
      ...race.progress,
      [vehicleId]: next,
    },
  }
}

export function rankRacers(race: RaceState, segmentProgress: Record<string, number>): string[] {
  const finishRank = new Map(race.finishOrder.map((id, index) => [id, index]))
  return [...race.vehicleIds].sort((a, b) => {
    const aFinish = finishRank.get(a)
    const bFinish = finishRank.get(b)
    if (aFinish !== undefined || bFinish !== undefined) {
      if (aFinish === undefined) return 1
      if (bFinish === undefined) return -1
      return aFinish - bFinish
    }
    const aProgress = race.progress[a]
    const bProgress = race.progress[b]
    if (!aProgress || !bProgress) return 0
    if (aProgress.lap !== bProgress.lap) return bProgress.lap - aProgress.lap
    if (aProgress.lastCheckpoint !== bProgress.lastCheckpoint) {
      return bProgress.lastCheckpoint - aProgress.lastCheckpoint
    }
    const progressDifference = (segmentProgress[b] ?? 0) - (segmentProgress[a] ?? 0)
    return progressDifference !== 0 ? progressDifference : race.vehicleIds.indexOf(a) - race.vehicleIds.indexOf(b)
  })
}

export function restartRace(race: RaceState): RaceState {
  return createRaceState(race.vehicleIds, race.totalLaps, race.playerId, race.checkpointCount)
}
